import { Button, Dropdown, Space } from "antd";
import { type JSX, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import ShowRosterFixtureAction from "~pages/Fixtures/FixtureListPage/components/Actions/ShowRosterFixtureAction";
import type { ColumnComponentProperties, FixtureTransformed } from "../../types";
import { DeleteFixtureAction, LockUnlockFixtureAction, ResetFixtureAction } from "../Actions";

interface IActionsColumnProperties extends ColumnComponentProperties {
  massEditMode: boolean;
  editFixtureId: string;
  onInlineEditClicked: (fixtureId: string) => void;
  onModalEditClicked: (fixture: FixtureTransformed) => void;
  onLockUnlockClicked: (fixtureId: string) => void;
}

type ActionMenuItem = {
  key: string;
  label: JSX.Element;
  disabled?: boolean;
};

export const ActionsColumn = (properties: IActionsColumnProperties) => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, seasonId, customerId } = useParams();

  const { hasPermission } = useUserPermissions();
  const hasPermissionToUpdateFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const hasPermissionToDeleteFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.DELETE);
  const hasPermissionToUpdateFixtureSettings = hasPermission(PermissionNames.FIXTURE_SETTINGS, PermissionOperations.UPDATE);

  const advancedActionsMenuItems: ActionMenuItem[] = [];

  if (hasPermissionToDeleteFixture) {
    advancedActionsMenuItems.push({
      key: "delete",
      label: <DeleteFixtureAction fixture={properties.value} />,
      disabled: properties.value.locked as boolean,
    });
  }
  if (hasPermissionToUpdateFixtureSettings) {
    advancedActionsMenuItems.push({
      key: "reset",
      label: <ResetFixtureAction fixtureId={properties.value.fixtureId} />,
      disabled: properties.value.locked as boolean,
    });

    advancedActionsMenuItems.push({
      key: "lock-unlock",
      label: <LockUnlockFixtureAction fixture={properties.value} onLockUnlockClicked={properties.onLockUnlockClicked} />,
    });

    advancedActionsMenuItems.push({
      key: "roster",
      label: <ShowRosterFixtureAction fixtureId={properties.value.fixtureId} />,
    });
  }

  const onInlineEditModeClickedHandler = useCallback(() => {
    if (properties.onInlineEditClicked) {
      properties.onInlineEditClicked(properties.editFixtureId ? "" : properties.value.fixtureId);
    }
  }, [properties]);

  const onModalEditModeClickedHandler = useCallback(() => {
    if (properties.onModalEditClicked) {
      properties.onModalEditClicked(properties.value as FixtureTransformed);
    }
  }, [properties]);

  const inlineEditIconClassName = useMemo(
    () => (`${properties.editFixtureId}` === `${properties.value.fixtureId}` ? "fas fa-pen-clip" : "fas fa-pen"),
    [properties],
  );

  return (
    <Space style={{ columnGap: "0px" }}>
      {hasPermissionToUpdateFixture && (
        <>
          {!properties.massEditMode && (
            <>
              <Link
                to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${properties.value.seasonId}/fixtures/${properties.value.fixtureId}/broadcast`}
                state={{
                  back: `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`,
                }}
              >
                <Button
                  type="text"
                  size="small"
                  title={formatMessage({
                    id: "fixtureListPage.table.column.actions.broadcast",
                  })}
                  icon={<i className="fas fa-tower-broadcast" />}
                />
              </Link>
              <Button
                onClick={() => onInlineEditModeClickedHandler()}
                type={"text"}
                size={"small"}
                title={formatMessage({
                  id: "fixtureListPage.table.column.actions.inlineEdit",
                })}
                icon={<i className={inlineEditIconClassName} />}
                disabled={properties.value.locked}
              />
              <Button
                onClick={() => onModalEditModeClickedHandler()}
                type={"text"}
                size={"small"}
                title={formatMessage({
                  id: "fixtureListPage.table.column.actions.modalEdit",
                })}
                icon={<i className="fas fa-pen-to-square" />}
                disabled={properties.value.locked}
              />
            </>
          )}
          <Link
            to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${properties.value.seasonId}/fixtures/${properties.value.fixtureId}`}
            state={{
              back: `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`,
            }}
          >
            <Button
              type="text"
              size="small"
              title={formatMessage({
                id: "fixtureListPage.table.column.actions.advancedEdit",
              })}
              icon={<i className="fas fa-cog" />}
              disabled={properties.value.locked}
            />
          </Link>
        </>
      )}
      {advancedActionsMenuItems.length > 0 && (
        <Dropdown
          menu={{
            items: advancedActionsMenuItems.map((item) => ({
              key: item.key,
              label: item.label,
            })),
          }}
        >
          <Button type={"text"} size={"small"} icon={<i className="fa-solid fa-ellipsis-vertical" />} />
        </Dropdown>
      )}
    </Space>
  );
};
